import React from 'react';
import stateGovernmentService from '../services/stateGovernmentService';
import dataService from '../services/dataService';
import OfficialDisplay, { OfficialsListDisplay } from './OfficialDisplay';
import { getPartyColor, getPartyLabel } from '../utils/partyUtils';
import { THEME } from '../constants/appConfig';

const StateGovernment = ({ selectedState }) => {
    if (!selectedState) {
        return (
            <div style={{
                padding: '20px',
                backgroundColor: '#f9f9f9',
                borderRadius: '8px',
                margin: '20px 0'
            }}>
                <h3 style={{ color: '#1E3A8A' }}>Government Information</h3>
                <p style={{ color: '#666', fontStyle: 'italic' }}>
                    Select a state to view government officials
                </p>
            </div>
        );
    }

    const stateName = dataService.getStateName(selectedState);
    const governmentData = stateGovernmentService.getStateGovernment(selectedState);
    const { 
        website: stateWebsite,
        governor,
        senators,
        houseSeats,
        representatives,
        legislature
    } = governmentData;

    // Component-specific styles
    const sectionStyle = {
        marginBottom: '25px',
        padding: '15px',
        backgroundColor: THEME.colors.light,
        borderRadius: '6px',
        border: `1px solid ${THEME.colors.border}`
    };

    const sectionHeaderStyle = (borderColor) => ({
        color: '#333',
        marginBottom: '10px',
        fontSize: '18px',
        borderLeft: `4px solid ${borderColor}`,
        paddingLeft: '10px'
    });

    return (
        <div style={{
            padding: '20px',
            backgroundColor: '#fff',
            borderRadius: '8px',
            margin: '20px 0',
            border: `2px solid ${THEME.colors.primary}`,
            boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
        }}>
            <h3 style={{ 
                color: THEME.colors.primary, 
                borderBottom: `2px solid ${THEME.colors.danger}`,
                paddingBottom: '10px',
                marginBottom: '20px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
            }}>
                <span>{stateName} Government Officials</span>
                <a 
                    href={stateWebsite}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                        fontSize: '14px',
                        color: THEME.colors.info,
                        textDecoration: 'none',
                        padding: '6px 12px',
                        backgroundColor: '#E8F4FD',
                        border: '1px solid #B0D4F1',
                        borderRadius: '6px',
                        transition: 'all 0.2s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.target.style.backgroundColor = '#D0E8FA';
                        e.target.style.borderColor = '#90C4F1';
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.backgroundColor = '#E8F4FD';
                        e.target.style.borderColor = '#B0D4F1';
                    }}
                >
                    Official State Website
                </a>
            </h3>

            {/* Remove age statistics summary - using consistent display instead */}

            {/* Governor Section */}
            {governor && (
                <div style={sectionStyle}>
                    <h4 style={sectionHeaderStyle(THEME.colors.danger)}>
                        Governor
                    </h4>
                    <OfficialDisplay official={governor} type="governor" />
                </div>
            )}

            {/* U.S. Senators Section */}
            {senators && senators.length > 0 && (
                <div style={sectionStyle}>
                    <h4 style={sectionHeaderStyle(THEME.colors.info)}>
                        U.S. Senators
                    </h4>
                    <OfficialsListDisplay officials={senators} type="senator" />
                </div>
            )}

            {/* U.S. House Representatives Section */}
            {houseSeats && (
                <div style={sectionStyle}>
                    <h4 style={sectionHeaderStyle(THEME.colors.success)}>
                        U.S. House of Representatives ({houseSeats} seats)
                    </h4>
                    {representatives && representatives.dataAvailable && representatives.representatives.length > 0 ? (
                        <div>
                            <OfficialsListDisplay officials={representatives.representatives} type="representative" />
                            {representatives.message && (
                                <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
                                    {representatives.message}
                                </p>
                            )}
                        </div>
                    ) : (
                        <div style={{
                            padding: '10px',
                            backgroundColor: 'white',
                            borderRadius: '4px',
                            border: `1px solid ${THEME.colors.border}`
                        }}>
                            <div style={{ fontSize: '14px', color: '#666', fontStyle: 'italic' }}>
                                {representatives?.message || `${houseSeats} House seats - Detailed representative data with ages coming soon`}
                            </div>
                            {houseSeats && (
                                <div style={{ marginTop: '10px', fontSize: '12px', color: '#999' }}>
                                    Full age and tenure data currently available for CA, TX, FL, and NY delegations.
                                </div>
                            )}
                        </div>
                    )}
                </div>
            )}

            {/* State Legislature Section */}
            {legislature && (
                <div style={sectionStyle}>
                    <h4 style={sectionHeaderStyle(THEME.colors.warning)}>
                        State Legislature Control
                    </h4>
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
                        gap: '10px'
                    }}>
                        <div style={{
                            padding: '10px',
                            backgroundColor: 'white',
                            borderRadius: '4px',
                            border: '1px solid #E0E0E0',
                            textAlign: 'center'
                        }}>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                                State Senate
                            </div>
                            <span style={{
                                display: 'inline-block',
                                padding: '4px 12px',
                                backgroundColor: getPartyColor(legislature.senate),
                                color: 'white',
                                borderRadius: '4px',
                                fontSize: '14px',
                                fontWeight: 'bold'
                            }}>
                                {getPartyLabel(legislature.senate)}
                            </span>
                        </div>
                        <div style={{
                            padding: '10px',
                            backgroundColor: 'white',
                            borderRadius: '4px',
                            border: '1px solid #E0E0E0',
                            textAlign: 'center'
                        }}>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                                State House
                            </div>
                            <span style={{
                                display: 'inline-block',
                                padding: '4px 12px',
                                backgroundColor: getPartyColor(legislature.house),
                                color: 'white',
                                borderRadius: '4px',
                                fontSize: '14px',
                                fontWeight: 'bold'
                            }}>
                                {getPartyLabel(legislature.house)}
                            </span>
                        </div>
                        {legislature.trifecta && (
                            <div style={{
                                padding: '10px',
                                backgroundColor: 'white',
                                borderRadius: '4px',
                                border: '1px solid #E0E0E0',
                                textAlign: 'center'
                            }}>
                                <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                                    Trifecta
                                </div>
                                <span style={{
                                    display: 'inline-block',
                                    padding: '4px 12px',
                                    backgroundColor: getPartyColor(legislature.trifecta),
                                    color: 'white',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    fontWeight: 'bold'
                                }}>
                                    {getPartyLabel(legislature.trifecta)}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {/* Data Source Note */}
            <div style={{
                marginTop: '20px',
                padding: '10px',
                backgroundColor: '#FFF9E6',
                borderRadius: '4px',
                border: '1px solid #FFD700',
                fontSize: '12px',
                color: '#666',
                textAlign: 'center'
            }}>
                Government data current as of 2024. Click any official's name to visit their website.
                <br />
                <span style={{ fontSize: '11px', fontStyle: 'italic' }}>
                    Note: Some newly elected officials may redirect to Senate.gov contact page until their official sites are established.
                </span>
            </div>
        </div>
    );
};

export default StateGovernment;